import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { cn } from '../lib/utils';

const CalendarView = ({ habits, completions }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };



  // Determine color based on completion rate: 0% (Red) -> 100% (Green)
  const getColorForRate = (rate) => {
    if (rate >= 100) return 'rgba(34, 197, 94, 1)'; // 100% = Emerald Green (Success)
    if (rate <= 0) return undefined;

    let r, g, b;

    // Breakpoints:
    // 0%: Red (239, 68, 68)
    // 60%: Yellow (234, 179, 8) - Shifted midpoint
    // 99%: Lime/YellowGreen (154, 205, 50) - Distinct from Emerald

    if (rate < 60) {
      // 0 -> 60: Red to Yellow
      const p = rate / 60;
      r = Math.round(239 + (234 - 239) * p);
      g = Math.round(68 + (179 - 68) * p);
      b = Math.round(68 + (8 - 68) * p);
    } else {
      // 60 -> 99: Yellow to Lime
      // We map 60..100 range effectively to Yellow..Lime to ensure <100 isn't Emerald
      const p = (rate - 60) / 40;

      // Yellow: 234, 179, 8
      // Lime: 154, 205, 50
      r = Math.round(234 + (154 - 234) * p);
      g = Math.round(179 + (205 - 179) * p);
      b = Math.round(8 + (50 - 8) * p);
    }

    return `rgba(${r}, ${g}, ${b}, 0.8)`;
  };


  const getCompletionRate = (dateStr) => {
    if (habits.length === 0) return 0;

    // Only count habits that existed on this date
    const dateObj = new Date(dateStr);
    const activeHabits = habits.filter(h => {
      const creationTime = h.created_at || h.createdAt;
      if (!creationTime) return true; // Assume active if no date
      const createdDate = new Date(creationTime);
      // Normalize to midnight just to be safe, though ISO string compare works for YYYY-MM-DD usually
      // If createdDate is AFTER dateStr, habit didn't exist
      return createdDate.toISOString().split('T')[0] <= dateStr;
    });

    if (activeHabits.length === 0) return 0;

    let totalScore = 0;

    activeHabits.forEach(habit => {
      const completion = completions.find(c => c.habitId === habit.id && c.date === dateStr);
      if (completion) {
        let score = 1;
        if (habit.target && completion.value) {
          score = Math.min(parseFloat(completion.value) / parseFloat(habit.target), 1);
        }
        totalScore += score;
      }
    });

    return (totalScore / activeHabits.length) * 100;
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);

  const previousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };


  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-foreground">
          {monthNames[month]} {year}
        </h2>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={previousMonth}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={nextMonth}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-2">
        {dayNames.map((day) => (
          <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
            {day}
          </div>
        ))}

        {Array.from({ length: startingDayOfWeek }).map((_, index) => (
          <div key={`empty-${index}`} className="aspect-square" />
        ))}

        {Array.from({ length: daysInMonth }).map((_, index) => {
          const day = index + 1;
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const completionRate = getCompletionRate(dateStr);
          const isToday = dateStr === todayStr;
          const isFuture = new Date(dateStr) > today;

          return (
            <div
              key={day}
              className={cn(
                'aspect-square rounded-lg border flex flex-col items-center justify-center',
                'transition-all duration-200 hover:shadow-md',
                isToday && 'ring-2 ring-primary',
                isFuture && 'opacity-40'
              )}
              style={{
                backgroundColor: !isFuture ? getColorForRate(completionRate) : undefined,
              }}
            >
              <span className={cn(
                'text-sm font-medium',
                completionRate >= 50 ? 'text-white' : 'text-foreground'
              )}>
                {day}
              </span>
              {completionRate > 0 && !isFuture && (
                <span className={cn(
                  'text-xs mt-1',
                  completionRate > 50 ? 'text-white' : 'text-muted-foreground'
                )}>
                  {Math.round(completionRate)}%
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 p-4 bg-muted rounded-lg">
        <h3 className="text-sm font-medium text-foreground mb-2">Legenda</h3>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded border" />
            <span>0%</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgb(239, 68, 68)' }} />
            <span>&lt; 50%</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgb(234, 179, 8)' }} />
            <span>~50%</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(34, 197, 94, 1)' }} />
            <span>100%</span>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default CalendarView;
